import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClients } from '@/hooks/useCrm';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';

const STATUS_COLORS = {
  'Doc Collection': 'bg-blue-50 text-blue-700',
  'Doc Drafting': 'bg-indigo-50 text-indigo-700',
  'Client Signing': 'bg-purple-50 text-purple-700',
  'Emigrate Submission': 'bg-amber-50 text-amber-700',
  'Police & Edu Verification': 'bg-amber-50 text-amber-700',
  'Pending Inspection': 'bg-orange-50 text-orange-700',
  'Pending Delhi': 'bg-orange-50 text-orange-700',
  'Deficiency': 'bg-red-50 text-red-700',
  'Awaiting Approval': 'bg-yellow-50 text-yellow-700',
  'Completed': 'bg-emerald-50 text-emerald-700',
};

const Clients = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { clients, loading } = useClients();

  const filtered = useMemo(() => {
    if (!query.trim()) return clients;
    const q = query.toLowerCase();
    return clients.filter((c) => {
      const co = c.company || {};
      return (
        co.company_name?.toLowerCase().includes(q) ||
        co.short_name?.toLowerCase().includes(q) ||
        co.district?.toLowerCase().includes(q) ||
        c.status?.toLowerCase().includes(q)
      );
    });
  }, [clients, query]);

  return (
    <div className="p-8" data-testid="clients-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Clients</h1>
          <p className="text-ink/60 mt-1">{clients.length} clients · search to filter</p>
        </div>
        <Button
          data-testid="new-client-btn"
          onClick={() => navigate('/clients/new')}
          className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Client
        </Button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="clients-search"
          placeholder="Search by company, short name, district, status..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold tracking-wide text-gray-600 ">
          <div className="col-span-5">Company Name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">District</div>
          <div className="col-span-3">Status</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {clients.length === 0
              ? 'No clients yet. Click "New Client" to add your first one.'
              : 'No clients match your search.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((c) => {
              const co = c.company || {};
              return (
                <button
                  key={c.id}
                  data-testid={`client-row-${c.id}`}
                  onClick={() => navigate(`/clients/${c.id}`)}
                  className="w-full text-left grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="col-span-5">
                    <div className="font-medium text-gray-900">{co.company_name || '—'}</div>
                    {co.short_name && (
                      <div className="text-xs text-gray-500">{co.short_name}</div>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-700 flex items-center">{co.type || '—'}</div>
                  <div className="col-span-2 text-sm text-gray-700 flex items-center">{co.district || '—'}</div>
                  <div className="col-span-3 flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>
                      {c.status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
